import { Circle, CircleDashed, InfoIcon } from "lucide-react";
import { ToothData } from "@/dentconnect/src/pages/job-request/types";
import {
	quadrant1,
	quadrant2,
	quadrant3,
	quadrant4,
} from "@/dentlab/src/components/odontogram/constants";
import { toothHasInfo as checkToothHasInfo } from "@/dentlab/src/components/odontogram/helpers";
import { FormField } from "@/dentlab/src/pages/form-builder/types/formTypes";

/**
 * Component to display odontogram data in messages
 * @param fieldValue - Array of ToothData objects representing selected teeth with their values
 * @param fieldId - ID of the field (used for references and as component key)
 * @param references - Map of field IDs to their display names
 * @param odontogramFields - Optional array of FormField definitions for the odontogram subfields
 */
export const OdontogramInMessage: React.FC<{
	fieldValue: ToothData[];
	fieldId: string;
	references: Record<string, string>;
	odontogramFields?: FormField[];
}> = ({ fieldValue, fieldId, references, odontogramFields }) => {
	// If no tooth data, show empty state
	if (!fieldValue || !Array.isArray(fieldValue) || fieldValue.length === 0) {
		return null;
	}

	// Use the standardized ToothData format
	const teethData: ToothData[] = fieldValue;
	const selectedToothNumbers: number[] = teethData.map((t) => t.toothNumber);

	// Helper to determine if a tooth has information
	const toothHasInfo = (toothNumber: number): boolean => {
		return checkToothHasInfo(toothNumber, true, teethData);
	};

	// Get the dropdown fields from the first tooth that has values
	const firstToothWithValues = teethData.find((t) =>
		toothHasInfo(t.toothNumber)
	);
	const dropdownFieldIds = firstToothWithValues
		? Object.keys(firstToothWithValues.values)
		: [];

	// Helper function to get a user-friendly display name for a field
	// Note that subfields of the odontogram are also pushed to the references of the form submission
	// So it works the same way as the other fields
	const getFieldLabel = (fieldId: string): string => {
		// If we have a reference label, use it
		if (references && references[fieldId]) {
			return references[fieldId];
		}
		return "Unbekannt";
	};

	// Helper function to format option values for display
	const getFormattedOptionValue = (
		fieldId: string,
		value: string
	): string => {
		if (!value || value === "") return "—";

		// Try to find the field definition to check its type
		const field = odontogramFields?.find((f) => f.id === fieldId);

		// If we have field type info, use it directly
		if (field?.type === "select") {
			// This is a SELECT field - parse as option index
			const index = parseInt(value, 10);
			if (!isNaN(index)) {
				const optionLabel = references[fieldId + "-" + index];
				if (optionLabel) return optionLabel;
			}
		} else if (field?.type === "text" || field?.type === "textarea") {
			// TEXT/TEXTAREA fields - return raw value
			return value;
		}

		// Fallback: Check if this field is a SELECT field by looking for option references
		// This handles cases where odontogramFields isn't provided (e.g., old messages)
		const hasOptionReferences = references[fieldId + "-0"] !== undefined;
		if (hasOptionReferences) {
			const index = parseInt(value, 10);
			if (!isNaN(index)) {
				const optionLabel = references[fieldId + "-" + index];
				if (optionLabel) return optionLabel;
			}
		}

		// Final fallback: return raw value
		return value;
	};

	// Get teeth with information for the table
	const teethWithInfo = teethData
		.filter((tooth) => toothHasInfo(tooth.toothNumber))
		.sort((a, b) => a.toothNumber - b.toothNumber);

	// Determine if we should show the detailed table
	const hasDetailedInfo =
		teethWithInfo.length > 0 && dropdownFieldIds.length > 0;

	// Mini tooth component for display
	const MiniTooth: React.FC<{
		number: number;
		selected: boolean;
		hasInfo?: boolean;
	}> = ({ number, selected, hasInfo = false }) => (
		<div
			className={`relative flex flex-col items-center ${
				selected ? "text-primary" : "text-gray-400"
			}`}
		>
			{selected ? (
				<>
					<Circle className="h-4 w-4 stroke-primary fill-primary/20" />
					{hasInfo && (
						<div className="absolute -top-0.5 -right-0.5">
							<InfoIcon className="h-2 w-2 text-blue-500 fill-blue-500" />
						</div>
					)}
				</>
			) : (
				<CircleDashed className="h-4 w-4" />
			)}
			<span className="text-[8px] font-medium">{number}</span>
		</div>
	);

	return (
		<div className="w-full" key={fieldId}>
			{/* Field label if provided */}
			{fieldId && references && references[fieldId] && (
				<div className="font-medium mb-1 text-sm">
					{getFieldLabel(fieldId)}
				</div>
			)}

			{/* Visual odontogram */}
			<div className="bg-white p-2 rounded-lg border border-gray-200 w-full mb-2">
				<div className="flex flex-col gap-3">
					{/* Top row: Q1 (18-11) and Q2 (21-28) */}
					<div className="grid grid-cols-2 gap-0 divide-x divide-gray-200">
						{/* Q1: Top Left (18-11) */}
						<div className="grid grid-cols-8 gap-0 pr-2">
							{quadrant1.map((toothNumber) => (
								<MiniTooth
									key={toothNumber}
									number={toothNumber}
									selected={selectedToothNumbers.includes(
										toothNumber
									)}
									hasInfo={toothHasInfo(toothNumber)}
								/>
							))}
						</div>

						{/* Q2: Top Right (21-28) */}
						<div className="grid grid-cols-8 gap-0 pl-2">
							{quadrant2.map((toothNumber) => (
								<MiniTooth
									key={toothNumber}
									number={toothNumber}
									selected={selectedToothNumbers.includes(
										toothNumber
									)}
									hasInfo={toothHasInfo(toothNumber)}
								/>
							))}
						</div>
					</div>

					{/* Divider for upper and lower teeth */}
					<div className="border-t border-gray-200" />

					{/* Bottom row: Q4 (48-41) and Q3 (31-38) */}
					<div className="grid grid-cols-2 gap-0 divide-x divide-gray-200">
						{/* Q4: Bottom Left (48-41) */}
						<div className="grid grid-cols-8 gap-0 pr-2">
							{quadrant4.map((toothNumber) => (
								<MiniTooth
									key={toothNumber}
									number={toothNumber}
									selected={selectedToothNumbers.includes(
										toothNumber
									)}
									hasInfo={toothHasInfo(toothNumber)}
								/>
							))}
						</div>

						{/* Q3: Bottom Right (31-38) */}
						<div className="grid grid-cols-8 gap-0 pl-2">
							{quadrant3.map((toothNumber) => (
								<MiniTooth
									key={toothNumber}
									number={toothNumber}
									selected={selectedToothNumbers.includes(
										toothNumber
									)}
									hasInfo={toothHasInfo(toothNumber)}
								/>
							))}
						</div>
					</div>
				</div>
			</div>

			{/* Show table for teeth with information if any teeth have values */}
			{hasDetailedInfo && (
				<div className="border rounded-md overflow-hidden mb-2">
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								<th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Zahn
								</th>
								{dropdownFieldIds.map((fieldId) => (
									<th
										key={fieldId}
										className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
									>
										{getFieldLabel(fieldId)}
									</th>
								))}
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{teethWithInfo.map((tooth) => (
								<tr key={tooth.toothNumber}>
									<td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900">
										{tooth.toothNumber}
									</td>
									{dropdownFieldIds.map((fieldId) => {
										const valueIndex =
											tooth.values[fieldId] || "";
										return (
											<td
												key={fieldId}
												className="px-2 py-1 whitespace-nowrap text-xs text-gray-500"
											>
												{valueIndex !== "" ? (
													<span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded-full">
														{getFormattedOptionValue(
															fieldId,
															valueIndex
														)}
													</span>
												) : (
													"—"
												)}
											</td>
										);
									})}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};
